import api from '../services/api';

export interface DomainFilters {
  expertise: boolean;
  field: boolean;
  domainFilter: boolean;
  skills: boolean;
  exclude: boolean;
  location: boolean;
  experience: boolean;
  companies: boolean;
  education: boolean;
  school: boolean;
  certificates: boolean;
  researchPapers: boolean;
  naturalLanguages: boolean;
  programmingLanguages: boolean;
  rate?: boolean;
}

export const DEFAULT_LOCATION_OPTIONS = ['United States', 'Canada', 'United Kingdom', 'Western Europe', 'Rest of World'];

export interface DomainAppConfig {
  showExperience: boolean;
  expertiseOptions: string[];
  fieldOptions: string[];
  domainFilterOptions: string[];
  locationOptions: string[];
  filters: DomainFilters;
  skillsConfig?: Record<string, { top: string[]; all: string[] }>;
}

const DEFAULT_FILTERS: DomainFilters = {
  expertise: false,
  field: false,
  domainFilter: false,
  skills: true,
  exclude: true,
  location: true,
  experience: false,
  companies: false,
  education: false,
  school: false,
  certificates: false,
  researchPapers: false,
  naturalLanguages: true,
  programmingLanguages: false,
  rate: false,
};

const DEFAULT_DOMAIN_CONFIG: DomainAppConfig = {
  showExperience: false,
  expertiseOptions: [],
  fieldOptions: [],
  domainFilterOptions: [],
  locationOptions: DEFAULT_LOCATION_OPTIONS,
  filters: DEFAULT_FILTERS,
};

// Module-level stores — populated by fetchAppConfig()
let domainConfigs: Record<string, DomainAppConfig> = {};
let domainLabels: Record<string, string> = {};
let inflightConfigRequest: Promise<string[]> | null = null;

/**
 * Fetch app config from the backend. Returns availableDomains.
 * Deduplicates concurrent calls — safe to call from StrictMode double-mount.
 */
export function fetchAppConfig(): Promise<string[]> {
  if (inflightConfigRequest) return inflightConfigRequest;
  inflightConfigRequest = (async () => {
    try {
      const res = await api.get<{
        success: boolean;
        config: { availableDomains: string[]; domainConfigs: Record<string, DomainAppConfig>; domainLabels?: Record<string, string> };
      }>('/config');
      if (res.data?.success && res.data.config) {
        domainConfigs = res.data.config.domainConfigs ?? {};
        domainLabels = res.data.config.domainLabels ?? {};
        return res.data.config.availableDomains ?? ['SWE'];
      }
    } catch {
      // fallback to defaults
    }
    return ['SWE'];
  })();
  inflightConfigRequest.finally(() => { inflightConfigRequest = null; });
  return inflightConfigRequest;
}

export function getDomainConfig(domain: string): DomainAppConfig {
  return domainConfigs[domain] ?? DEFAULT_DOMAIN_CONFIG;
}

export function getFilterVisibility(domain: string): DomainFilters {
  return domainConfigs[domain]?.filters ?? DEFAULT_FILTERS;
}

const DOMAIN_DISPLAY_LABELS: Record<string, string> = {
  SWE: 'Software Engineering',
  STEM: 'STEM',
  Finance: 'Investment Banking & Private Equity',
  DSML: 'Data Science & Machine Learning',
};

const DOMAIN_SEARCH_PLACEHOLDERS: Record<string, string> = {
  SWE: 'Try "Python developer with 4+ yrs of experience"',
  STEM: 'Try "PhD researcher with Python and MATLAB experience"',
  Finance: 'Try "Investment Banker with Financial Modeling skills"',
  DSML: 'Try "ML engineer with PyTorch and NLP experience"',
};

const DEFAULT_SEARCH_PLACEHOLDER = 'Search for talent...';

/** Returns a user-friendly display label for a domain code (e.g. SWE → Software Engineers). */
export function getDomainLabel(domain: string): string {
  const lower = domain.toLowerCase();
  const fromApi =
    domainLabels[domain] ??
    domainLabels[lower] ??
    Object.entries(domainLabels).find(([k]) => k.toLowerCase() === lower)?.[1];
  return fromApi ?? DOMAIN_DISPLAY_LABELS[domain] ?? domain;
}

const NON_ACADEMIC_DOMAINS = new Set(['SWE', 'Finance']);

/** Returns true for STEM-like domains (publications, academic verification layout). All domains except SWE and Finance. */
export function isAcademicDomain(domain: string): boolean {
  return !NON_ACADEMIC_DOMAINS.has(domain);
}

/** Returns a search bar placeholder appropriate for the given domain. */
export function getDomainPlaceholder(domain: string): string {
  return DOMAIN_SEARCH_PLACEHOLDERS[domain] ?? DEFAULT_SEARCH_PLACEHOLDER;
}

/**
 * Resolve a URL domain value (label or code) back to an internal domain code.
 * Checks against availableDomains by code match first, then label match.
 */
export function resolveDomainCode(value: string, availableDomains: string[]): string | undefined {
  if (availableDomains.includes(value)) return value;
  const lower = value.toLowerCase();
  return availableDomains.find(d => getDomainLabel(d).toLowerCase() === lower);
}
